import { useAuthStore } from '../stores/authStore';
import { USER_ROLES } from '../lib/constants/roles';

// Stable empty reference — Zustand selector returning `?? []` inline produces a
// new array each call, which `useSyncExternalStore` flags as a snapshot change
// and React 19 surfaces as "Maximum update depth exceeded". A module-scoped
// sentinel keeps the snapshot referentially stable when permissions is missing
// (e.g. a persisted user from before the permissions feature shipped).
const EMPTY_PERMISSIONS: readonly string[] = Object.freeze([]);

export function usePermissions() {
  const rawPermissions = useAuthStore((s) => s.user?.permissions ?? EMPTY_PERMISSIONS);
  // Defensive: a malformed or stale persisted store might yield a non-array here
  // (e.g. a test mock that ignores the selector). Treat anything that is not an
  // array as empty so has/hasAny/hasAll never throw and the layout never crashes
  // via the React Router error boundary.
  const permissions = Array.isArray(rawPermissions) ? rawPermissions : EMPTY_PERMISSIONS;
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === USER_ROLES.TENANT_ADMIN;

  const has = (key: string): boolean => isAdmin || permissions.includes(key);
  const hasAny = (keys: string[]): boolean => keys.some(has);
  const hasAll = (keys: string[]): boolean => keys.every(has);

  return { permissions, role, isAdmin, has, hasAny, hasAll };
}
